/* eslint-disable react/no-multi-comp */
/* eslint-disable jsx-a11y/label-has-for */

import React, { PureComponent } from 'react'
import { Tooltip } from 'shared/ui'
import ResizeObserver from 'resize-observer-polyfill'
import { debounce } from 'util/functions'
import cn from 'classnames'

const avatarComponent = agent => {
  if (!agent) return null
  const avatarUrl = agent.avatarUrl
  const actorInitials = agent.initials
  const styles = { backgroundImage: `url(${avatarUrl})` }
  return (
    <div className="avatar" style={styles}>
      {!avatarUrl && actorInitials}
    </div>
  )
}

const snippetOrCollisionIndicator = ({
  hasCollision,
  collidingAgent,
  collisionType,
  collisionOperation,
  snippetAuthor,
  snippetText,
  snippetType,
  hasDraft,
}) => {
  if (!hasCollision) {
    return (
      <span className={cn('snippet', snippetType)}>
        {!hasDraft &&
          snippetAuthor && (
            <span className="author">
              <span>{snippetAuthor}</span>:{' '}
            </span>
          )}
        {hasDraft && <span className="author">Draft: </span>}
        {snippetText}
      </span>
    )
  }
  return (
    <div className={cn('collision', collisionType)}>
      {avatarComponent(collidingAgent)}
      <div className="chip">
        <span className="name">
          {collidingAgent && collidingAgent.shortName}
        </span>{' '}
        {collisionOperation}
      </div>
    </div>
  )
}

class LabelBox extends React.PureComponent {
  constructor(props) {
    super(props)
    this.labelContainer = React.createRef()
    this.resizeTimeout = null
  }

  state = { shouldExpand: false }

  componentDidMount() {
    this.handleResize()
    this.observer = new ResizeObserver(this.handleResize)
    const labelContainer = this.labelContainer && this.labelContainer.current
    this.observer.observe(labelContainer)
  }

  componentWillUnmount() {
    this.removeObserver()
  }

  handleResize = debounce(() => {
    const labelContainer = this.labelContainer && this.labelContainer.current
    if (!labelContainer) return
    const height = labelContainer.clientHeight
    const shouldExpand = height > 24
    if (shouldExpand !== this.state.shouldExpand)
      this.setState({ shouldExpand })
  }, 100)

  removeObserver() {
    this.observer.disconnect()
    this.observer = null
  }

  render() {
    const { labelsJSON } = this.props
    const { shouldExpand } = this.state
    const labels = JSON.parse(labelsJSON)

    return (
      <div className="labelbox">
        <div className="labels">
          <div className="labels-inner" ref={this.labelContainer}>
            {labels.map(({ name, color }) => {
              return (
                <span
                  key={name}
                  className="label"
                  style={{
                    color,
                    backgroundColor: `${color}26`,
                  }}
                >
                  {name}
                </span>
              )
            })}
          </div>
        </div>
        {shouldExpand && (
          <Tooltip
            className={shouldExpand ? 'tooltip visible' : 'tooltip hidden'}
            position={'left'}
          >
            <span className="more">+ more</span>
          </Tooltip>
        )}
      </div>
    )
  }
}

class InnerListItem extends PureComponent {
  onClick = e => {
    const { conversationId, navigateToConversation } = this.props
    if (e.metaKey || e.ctrlKey) {
      return true
    }
    e.preventDefault()
    e.stopPropagation()
    navigateToConversation(conversationId)
    return false
  }

  onCheckboxClick = e => {
    e.stopPropagation()
    const { conversationId, toggleConversationSelection } = this.props
    toggleConversationSelection(conversationId)
  }

  onCloseOrOpenClick = e => {
    const {
      conversationId,
      isCloseable,
      openConversation,
      closeConversation,
    } = this.props
    e.stopPropagation()
    e.preventDefault()
    // eslint-disable-next-line no-unused-expressions
    isCloseable
      ? closeConversation(conversationId)
      : openConversation(conversationId)
  }

  onUnreadIndicatorClick = e => {
    const { conversationId, isUnread, markAsRead, markAsUnread } = this.props
    e.stopPropagation()
    e.preventDefault()
    // eslint-disable-next-line no-unused-expressions
    isUnread ? markAsRead(conversationId) : markAsUnread(conversationId)
  }

  render() {
    if (!this.props.present) return null
    const {
      conversationId,
      assignmentLabel,
      customerLabelFull,
      messageCount,
      subject,
      snippetText,
      snippetAuthor,
      snippetType,
      isCloseable,
      timestamp,
      shouldShowLabels,
      hasCollision,
      collidingAgent,
      collisionType,
      collisionOperation,
      hasAttachments,
      isStarred,
      mailboxColor,
      hasDraft,
      labelsJSON,
      selected,
    } = this.props
    return (
      <React.Fragment>
        <div className="right-side">
          <div className="quick-actions">
            <span role="button" onClick={this.onCloseOrOpenClick}>
              {isCloseable ? 'Close' : 'Open'}
            </span>
            <div
              className="ui checkbox ConversationListItem_bulkIndicator"
              onClick={this.onCheckboxClick}
            >
              <input type="checkbox" checked={selected} />
              <label />
            </div>
          </div>
          <div className="datetime">{timestamp}</div>
        </div>
        {/* don't copy div.right-side (or any element with an event) into anchor tag below
            Because, inner element's click event will also trigger <a> clicked event causing page to navigate
            https://bugzilla.mozilla.org/show_bug.cgi?id=62151 */}
        <a href={`/tickets/${conversationId}`} onClick={this.onClick}>
          {mailboxColor && (
            <div className="mailbox-indicator">
              <div
                className="inner"
                style={{ backgroundColor: mailboxColor }}
              />
            </div>
          )}
          <div
            className="unread-indicator"
            onClick={this.onUnreadIndicatorClick}
          />
          <div className="header">
            <div className="customer">{customerLabelFull}</div>
            {hasAttachments && <span className="Icon Icon-attachment" />}
            {isStarred && <span className="Icon Icon-star" />}
            <div className="icons" />
          </div>
          <div className="message">
            <div className="contents">
              <div className="subject">{subject}</div>
              {snippetOrCollisionIndicator({
                hasCollision,
                collidingAgent,
                collisionType,
                collisionOperation,
                snippetAuthor,
                snippetText,
                snippetType,
                hasDraft,
              })}
            </div>
            {messageCount > 1 && <div className="count">{messageCount}</div>}
          </div>
          {(assignmentLabel || shouldShowLabels) && (
            <div className="meta">
              {assignmentLabel && (
                <div className="assignment">{assignmentLabel}</div>
              )}
              {shouldShowLabels && <LabelBox labelsJSON={labelsJSON} />}
            </div>
          )}
        </a>
      </React.Fragment>
    )
  }
}

export default InnerListItem
